import {CommonModule} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';

import {ClientStateService} from '../../../services/auth/client-state.service';

@Component({
  selector: 'app-interface-load-repro',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <main class="interface-load-repro" [class.interface-load-repro--dashboard]="fixtureTarget === 'dashboard'">
      @if (fixtureState !== 'beta-unavailable') {
        <section class="repro-surface repro-surface--placeholder" data-testid="shell-load-background" aria-hidden="true"></section>
      } @else if (fixtureTarget === 'dashboard') {
        <section class="repro-surface repro-surface--dashboard" aria-label="Beta interface load repro dashboard">
          <div class="repro-header">
            <p class="repro-eyebrow">Beta unavailable</p>
            <h1>Shine AI dashboard</h1>
            <p class="repro-copy">
              Some report sources could not be loaded. The available sections are still shown.
            </p>
          </div>
          <div class="repro-grid">
            <article class="repro-card">
              <h2>Chatbots</h2>
              <p>Widget engagement and answer volume are still visible.</p>
            </article>
            <article class="repro-card">
              <h2>Assistants</h2>
              <p>Routing, response quality, and error pressure are still visible.</p>
            </article>
            <article class="repro-card">
              <h2>Tasks</h2>
              <p>Completion volume, failures, and execution time are still visible.</p>
            </article>
            <article class="repro-card">
              <h2>Workflows</h2>
              <p>Automation throughput, reliability, and duration are still visible.</p>
            </article>
          </div>
        </section>
      } @else {
        <section class="repro-surface repro-surface--chatbots" data-testid="beta-backend-unavailable" aria-label="Beta chatbot list unavailable">
          <div class="repro-icon" aria-hidden="true">☁</div>
          <div class="beta-unavailable-copy">
            <p class="repro-eyebrow">Beta unavailable</p>
            <h1>Chatbots are temporarily unavailable</h1>
            <p>
              We could not load the beta chatbot list right now. The backend may still be waking up,
              updating, or temporarily unreachable. Nothing was changed.
            </p>
          </div>
          <div class="repro-actions">
            <button type="button" class="repro-button repro-button--primary" (click)="reloadPage()">Reload page</button>
            <a
              class="repro-button repro-button--secondary"
              [routerLink]="['/ai/dashboard']"
              [queryParams]="{interfaceLoadVisualFixture: 'beta-unavailable', interfaceLoadFixtureTarget: 'dashboard'}">
              Go to dashboard
            </a>
          </div>
        </section>
      }
    </main>
  `,
  styles: [`
    .interface-load-repro {
      min-height: 100vh;
      padding: 24px;
      background: #f5f7fb;
      color: #162033;
    }

    .repro-surface {
      max-width: 1280px;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #dbe2ee;
      border-radius: 12px;
      box-shadow: 0 16px 40px rgba(22, 32, 51, 0.08);
      padding: 24px;
    }

    .repro-surface--chatbots {
      display: grid;
      gap: 20px;
      align-items: center;
      grid-template-columns: 64px minmax(0, 1fr) auto;
    }

    .repro-icon {
      display: grid;
      place-items: center;
      width: 64px;
      height: 64px;
      border-radius: 16px;
      background: #eef3ff;
      font-size: 28px;
    }

    .repro-header h1,
    .beta-unavailable-copy h1 {
      margin: 0;
      font-size: 28px;
      line-height: 1.2;
    }

    .repro-eyebrow {
      margin: 0 0 8px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: #596780;
    }

    .repro-copy,
    .beta-unavailable-copy p,
    .repro-card p {
      margin: 12px 0 0;
      color: #5e6b7f;
      line-height: 1.5;
    }

    .repro-grid {
      margin-top: 24px;
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .repro-card {
      border: 1px solid #dbe2ee;
      border-radius: 10px;
      padding: 18px;
      background: #fbfcfe;
    }

    .repro-card h2 {
      margin: 0;
      font-size: 18px;
      line-height: 1.3;
    }

    .repro-actions {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .repro-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 44px;
      padding: 0 16px;
      border-radius: 999px;
      border: 1px solid transparent;
      text-decoration: none;
      font-weight: 600;
      cursor: pointer;
    }

    .repro-button--primary {
      background: #1d4ed8;
      color: #fff;
    }

    .repro-button--secondary {
      background: #fff;
      color: #1d4ed8;
      border-color: #c8d2e2;
    }

    @media (max-width: 720px) {
      .interface-load-repro {
        padding: 16px;
      }

      .repro-surface--chatbots,
      .repro-grid {
        grid-template-columns: 1fr;
      }

      .repro-surface--chatbots {
        display: block;
      }

      .repro-actions {
        justify-content: stretch;
      }

      .repro-button {
        width: 100%;
      }
    }
  `]
})
export class InterfaceLoadReproComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly clientStateService = inject(ClientStateService);

  readonly fixtureTarget = this.route.snapshot.data['fixtureTarget'] === 'chatbots' ? 'chatbots' : 'dashboard';
  readonly fixtureState = this.route.snapshot.queryParamMap.get('interfaceLoadVisualFixture') || 'beta-unavailable';

  ngOnInit(): void {
    this.clientStateService.setClientLoading(false);
    this.clientStateService.setBetaBackendUnavailable(false);
  }

  reloadPage(): void {
    window.location.reload();
  }
}
